import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Alert,
  Spinner,
  Badge,
  Button,
  ProgressBar,
  Table,
  Cards,
  PieChart,
  BarChart,
  LineChart,
  KeyValuePairs
} from '@cloudscape-design/components';

function S3StorageMetrics({ costData }) {
  const s3Data = costData?.s3Storage;
  const hasMetrics = s3Data && s3Data.reportDate && s3Data.storage?.currentBytes > 0;

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatCurrency = (amount) => {
    if (amount === 0) return '$0.00';
    if (amount < 0.01) return '< $0.01';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  if (!hasMetrics) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Alert type="info">
            <Box variant="strong">Storage metrics not available yet</Box>
            <Box variant="p" padding={{ top: 's' }}>
              S3 Storage Lens generates daily reports with detailed metrics about your storage usage, 
              requests, bandwidth, and costs. The first report will be available 24-48 hours after 
              your first upload.
            </Box>
            <Box variant="p" padding={{ top: 's' }}>
              Once available, you'll see:
            </Box>
            <ul>
              <li>Detailed storage breakdown and object counts</li>
              <li>Request activity (GET, PUT, HEAD, etc.)</li>
              <li>Data transfer metrics</li>
              <li>Cost breakdown by category</li>
            </ul>
          </Alert>
        </SpaceBetween>
      </Container>
    );
  }

  const metricsData = {
    storage: s3Data.storage,
    requests: s3Data.requests,
    bandwidth: s3Data.bandwidth,
    errors: s3Data.errors,
    costs: s3Data.costs,
    reportDate: s3Data.reportDate,
    daysWithData: s3Data.daysWithData
  };

  const encryptionPercentage = 100; // All objects are encrypted in our bucket
  const errorRate = metricsData.requests.total > 0
    ? ((metricsData.errors['4xx'] + metricsData.errors['5xx']) / metricsData.requests.total) * 100
    : 0;

  // Prepare chart data
  const requestsChartData = [
    { title: 'GET', value: metricsData.requests.get, color: '#0073bb' },
    { title: 'PUT', value: metricsData.requests.put, color: '#037f0c' },
    { title: 'HEAD', value: metricsData.requests.head, color: '#d13212' },
    { title: 'LIST', value: metricsData.requests.list, color: '#8b0000' },
    { title: 'DELETE', value: metricsData.requests.delete, color: '#ff9900' },
    { title: 'POST', value: metricsData.requests.post, color: '#ec7211' }
  ];

  const costBreakdownData = [
    { title: 'Storage', value: metricsData.costs.storage, color: '#0073bb' },
    { title: 'Requests', value: metricsData.costs.requests, color: '#037f0c' },
    { title: 'Data Transfer', value: metricsData.costs.transfer, color: '#ff9900' }
  ];

  return (
    <SpaceBetween size="l">
      {/* Storage Overview */}
      <Container>
        <Header variant="h2">Storage Overview</Header>
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Current Storage</Box>
            <Box variant="h2" color="text-status-info">
              {formatBytes(metricsData.storage.currentBytes)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              {metricsData.storage.currentGB.toFixed(2)} GB
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Total Objects</Box>
            <Box variant="h2">
              {formatNumber(metricsData.storage.currentObjects)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              Average: {metricsData.storage.currentObjects > 0 ? formatBytes(metricsData.storage.currentBytes / metricsData.storage.currentObjects) : '0 B'}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Encryption Status</Box>
            <Box variant="h2" color="text-status-success">
              {encryptionPercentage.toFixed(0)}%
            </Box>
            <Box variant="small" color="text-body-secondary">
              All objects encrypted
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Month-to-Date Cost</Box>
            <Box variant="h2" color="text-status-info">
              {formatCurrency(metricsData.costs.total)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              {metricsData.daysWithData} days tracked
            </Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Request Metrics */}
      <Container>
        <Header variant="h2">Request Activity (Last 24 Hours)</Header>
        <SpaceBetween size="l">
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Total Requests</Box>
              <Box variant="h2">{formatNumber(metricsData.requests.total)}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Error Rate</Box>
              <Box variant="h2" color={errorRate > 1 ? "text-status-error" : "text-status-success"}>
                {errorRate.toFixed(2)}%
              </Box>
              <Box variant="small" color="text-body-secondary">
                4xx: {metricsData.errors['4xx']}, 5xx: {metricsData.errors['5xx']}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Most Common</Box>
              <Box variant="h3">
                <Badge color="blue">HEAD</Badge>
              </Box>
              <Box variant="small" color="text-body-secondary">
                {formatNumber(metricsData.requests.head)} requests
              </Box>
            </div>
          </ColumnLayout>

          <PieChart
            data={requestsChartData}
            detailPopoverContent={(datum) => [
              { key: 'Request Type', value: datum.title },
              { key: 'Count', value: formatNumber(datum.value) },
              { key: 'Percentage', value: `${((datum.value / metricsData.requests.total) * 100).toFixed(1)}%` }
            ]}
            segmentDescription={(datum, sum) => 
              `${datum.value} requests, ${((datum.value / sum) * 100).toFixed(0)}%`
            }
            ariaLabel="Request distribution pie chart"
            ariaDescription="Pie chart showing distribution of S3 request types"
            size="medium"
            hideFilter
            empty={
              <Box textAlign="center" color="inherit">
                <b>No data available</b>
              </Box>
            }
          />
        </SpaceBetween>
      </Container>

      {/* Bandwidth Usage */}
      <Container>
        <Header variant="h2">Data Transfer</Header>
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Downloaded (Outbound)</Box>
            <Box variant="h2" color="text-status-info">
              {formatBytes(metricsData.bandwidth.downloaded)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              Cost: {formatCurrency(metricsData.costs.transfer)}
            </Box>
            <Box padding={{ top: 's' }}>
              <ProgressBar
                value={
                  (metricsData.bandwidth.downloaded + metricsData.bandwidth.uploaded) > 0
                    ? (metricsData.bandwidth.downloaded / (metricsData.bandwidth.downloaded + metricsData.bandwidth.uploaded)) * 100
                    : 0
                }
                variant="standalone"
                label="Download ratio"
              />
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Uploaded (Inbound)</Box>
            <Box variant="h2" color="text-status-success">
              {formatBytes(metricsData.bandwidth.uploaded)}
            </Box>
            <Box variant="small" color="text-body-secondary">
              Free (no charge for uploads)
            </Box>
            <Box padding={{ top: 's' }}>
              <ProgressBar
                value={
                  (metricsData.bandwidth.downloaded + metricsData.bandwidth.uploaded) > 0
                    ? (metricsData.bandwidth.uploaded / (metricsData.bandwidth.downloaded + metricsData.bandwidth.uploaded)) * 100
                    : 0
                }
                variant="standalone"
                label="Upload ratio"
              />
            </Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Cost Breakdown */}
      <Container>
        <Header variant="h2">Cost Breakdown (Month-to-Date)</Header>
        <SpaceBetween size="l">
          <PieChart
            data={costBreakdownData}
            detailPopoverContent={(datum) => [
              { key: 'Category', value: datum.title },
              { key: 'Cost', value: formatCurrency(datum.value) },
              { key: 'Percentage', value: `${((datum.value / metricsData.costs.total) * 100).toFixed(1)}%` }
            ]}
            segmentDescription={(datum, sum) => 
              `${formatCurrency(datum.value)}, ${((datum.value / sum) * 100).toFixed(0)}%`
            }
            ariaLabel="Cost breakdown pie chart"
            ariaDescription="Pie chart showing S3 cost distribution"
            size="medium"
            hideFilter
          />

          <Table
            columnDefinitions={[
              {
                id: 'category',
                header: 'Cost Category',
                cell: item => item.category
              },
              {
                id: 'mtd',
                header: 'Month-to-Date',
                cell: item => formatCurrency(item.mtd)
              },
              {
                id: 'percentage',
                header: 'Percentage',
                cell: item => `${item.percentage.toFixed(1)}%`
              }
            ]}
            items={[
              {
                category: 'Storage (Prorated Daily)',
                mtd: metricsData.costs.storage,
                percentage: (metricsData.costs.storage / metricsData.costs.total) * 100
              },
              {
                category: 'API Requests (All Types)',
                mtd: metricsData.costs.requests,
                percentage: (metricsData.costs.requests / metricsData.costs.total) * 100
              },
              {
                category: 'Data Transfer Out',
                mtd: metricsData.costs.transfer,
                percentage: (metricsData.costs.transfer / metricsData.costs.total) * 100
              }
            ]}
            variant="embedded"
          />
        </SpaceBetween>
      </Container>

      {/* Data Freshness Info */}
      <Alert type="info">
        <Box variant="small">
          <strong>Latest Report:</strong> {metricsData.reportDate || 'N/A'} ({metricsData.daysWithData} days tracked this month)
          <br />
          <strong>Cost Calculation:</strong> Storage costs are prorated daily and summed for accurate month-to-date totals. 
          Request and transfer costs are actual cumulative totals from all days in the billing period.
          <br />
          Metrics are updated daily from S3 Storage Lens with a 24-hour delay.
        </Box>
      </Alert>
    </SpaceBetween>
  );
}

export default S3StorageMetrics;
